import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '../api/client'
import { TOKEN_KEYS } from '../lib/constants'
import type { User, LoginRequest, RegisterRequest } from '../types'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (data: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN)
      if (token) {
        try {
          const userData = await apiClient.getMe()
          setUser(userData)
        } catch (error) {
          console.error('Failed to restore session:', error)
          apiClient.logout()
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = async (data: LoginRequest) => {
    await apiClient.login(data)
    const userData = await apiClient.getMe()
    setUser(userData)
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(userData))
  }

  const register = async (data: RegisterRequest) => {
    await apiClient.register(data)
    const userData = await apiClient.getMe()
    setUser(userData)
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(userData))
  }

  const logout = () => {
    apiClient.logout()
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const userData = await apiClient.getMe()
      setUser(userData)
      localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(userData))
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
